import { useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  Lock,
  Users,
  Wallet,
  ScrollText,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Network,
} from 'lucide-react';
import { useAuth } from '../state/AuthContext';
import { usePlant } from '../state/PlantContext';
import { Logo } from './Logo';

const NAV_ITEMS = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/app/permits', label: 'Permits', icon: ClipboardList },
  { to: '/app/isolation', label: 'Isolation & LOTO', icon: Lock },
  { to: '/app/topology', label: 'Plant Topology', icon: Network },
  { to: '/app/billing', label: 'Billing & Usage', icon: Wallet },
  { to: '/app/audit', label: 'Audit Log', icon: ScrollText, roles: ['platform_admin', 'org_admin', 'safety_officer'] },
  { to: '/app/admin', label: 'Admin', icon: Users, roles: ['org_admin', 'platform_admin'] },
];

function initials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { plants, currentPlant, setCurrentPlantId } = usePlant();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [plantMenuOpen, setPlantMenuOpen] = useState(false);

  const visibleItems = NAV_ITEMS.filter((item) => !item.roles || (user && item.roles.includes(user.role)));

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="app-shell">
      <aside
        style={{
          background: 'var(--navy)',
          color: 'var(--text-on-dark)',
          padding: '24px 16px',
          display: 'flex',
          flexDirection: 'column',
          position: mobileOpen ? 'fixed' : undefined,
          inset: mobileOpen ? 0 : undefined,
          zIndex: mobileOpen ? 200 : undefined,
          overflowY: 'auto',
        }}
        className={`app-sidebar${mobileOpen ? ' mobile-open' : ''}`}
      >
        <div className="row" style={{ marginBottom: 28, paddingLeft: 4 }}>
          <NavLink to="/" className="row gap-2" style={{ color: '#fff', fontWeight: 800, fontSize: '1.0625rem' }}>
            <Logo size={24} />
            PermitGuard AI
          </NavLink>
          <div className="spacer" />
          <button className="btn btn-ghost btn-icon" style={{ color: '#cfd9e8', display: mobileOpen ? 'inline-flex' : 'none' }} onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <X size={20} />
          </button>
        </div>

        {plants.length > 0 && (
          <div style={{ position: 'relative', marginBottom: 20 }}>
            <button
              className="row gap-2 w-full"
              onClick={() => setPlantMenuOpen((v) => !v)}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: 8,
                padding: '10px 12px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.8125rem',
                textAlign: 'left',
              }}
            >
              <div className="stack" style={{ minWidth: 0 }}>
                <span style={{ fontSize: '0.6875rem', color: '#8fa2bd', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Plant</span>
                <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentPlant?.name ?? 'Select plant'}</span>
              </div>
              <div className="spacer" />
              <ChevronDown size={16} />
            </button>
            {plantMenuOpen && (
              <div
                style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6, background: '#fff',
                  borderRadius: 8, boxShadow: 'var(--shadow-overlay)', overflow: 'hidden', zIndex: 10,
                }}
              >
                {plants.map((p) => (
                  <button
                    key={p.id}
                    className="w-full"
                    style={{ display: 'block', textAlign: 'left', padding: '10px 12px', border: 'none', background: p.id === currentPlant?.id ? 'var(--accent-soft)' : '#fff', color: 'var(--text)', cursor: 'pointer', fontSize: '0.875rem' }}
                    onClick={() => { setCurrentPlantId(p.id); setPlantMenuOpen(false); }}
                  >
                    {p.name} <span className="mono text-tertiary" style={{ fontSize: '0.75rem' }}>({p.code})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <nav className="stack gap-1">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `row gap-2 sidebar-link${isActive ? ' active' : ''}`}
              style={({ isActive }) => ({
                padding: '10px 12px',
                borderRadius: 8,
                color: isActive ? '#fff' : '#c3cfe1',
                background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                fontSize: '0.9375rem',
                fontWeight: 500,
              })}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="spacer" />

        <div style={{ marginTop: 32, borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 16 }}>
          <div className="row gap-2" style={{ marginBottom: 12 }}>
            <div className="avatar" style={{ background: 'rgba(255,255,255,0.14)', color: '#fff' }}>{initials(user?.name ?? null)}</div>
            <div className="stack" style={{ minWidth: 0 }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</span>
              <span style={{ fontSize: '0.75rem', color: '#8fa2bd' }}>{user?.role.replace(/_/g, ' ')}</span>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm w-full" style={{ color: '#cfd9e8', justifyContent: 'flex-start' }} onClick={handleLogout}>
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <div className="app-content">
        <div className="row app-topbar" style={{ display: 'none', padding: '14px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
          <button className="btn btn-ghost btn-icon" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <Menu size={22} />
          </button>
          <div className="row gap-2" style={{ marginLeft: 12, fontWeight: 700 }}>
            <Logo size={20} /> PermitGuard AI
          </div>
        </div>
        <main className="app-main">{children}</main>
      </div>

      <style>{`
        @media (max-width: 960px) {
          .app-sidebar { display: none !important; }
          .app-sidebar.mobile-open { display: flex !important; }
          .app-topbar { display: flex !important; align-items: center; }
        }
      `}</style>
    </div>
  );
}
